import { useQueryClient } from "@tanstack/react-query";
import { Check, Copy, CreditCard, Info, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { CheckoutInvoiceFields } from "@/components/features/payments/checkout-invoice-fields";
import { CheckoutSummaryPanel } from "@/components/features/payments/checkout-summary-panel";
import { serverMessage } from "@/lib/serverMessage";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  getBankTransferAccount,
  switchCheckoutToCard,
  getCertificateCheckout,
  uploadTransferReceipt,
  type BankTransferAccountResponse,
} from "@/services/certificatePaymentsService";
import { runCertificatePaymentResumeFlow } from "@/utils/certificatePaymentResumeRunner";

/**
 * Where a consumer paying by bank transfer completes the payment.
 *
 * Unlike the dLocal path there is no redirect and nothing to poll: the money
 * moves in the consumer's own banking app, and the only thing that comes back
 * to us is a comprobante they upload here. Uploading is what creates their
 * request — held server-side until the transfer is confirmed — so this page
 * must not be reachable in a state where the upload silently does nothing.
 *
 * The account details are FETCHED here rather than handed over by the wizard.
 * The hand-off is a full page load, which discards history state, and the
 * consumer may well refresh this page or come back to it later with their
 * banking app open. The per-checkout reference comes off the checkout itself.
 *
 * This route sits outside any layout (see App.tsx), so the brand header and the
 * back affordance are the page's own. Without them a consumer is asked to send
 * real money from a screen carrying no mark of whose site it is, and with no
 * way out — which is precisely the moment that reassurance matters most.
 */

const ACCEPTED = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";

/**
 * Checkout ids are UUIDs. Validating before the fetch turns a mistyped or
 * truncated link into this page's own "we couldn't find this payment" screen,
 * rather than a backend error the consumer sees as a broken site — while they
 * are mid-payment, which is the worst possible moment for that.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuidParam(value: string | null | undefined): boolean {
  return typeof value === "string" && UUID_RE.test(value.trim());
}

type Phase = "instructions" | "uploading" | "creating" | "createFailed";

function formatAmount(amountMinor: number, currency: string): string {
  const major = amountMinor / 100;
  // es-UY renders 3140 as "3.140,00", matching what the consumer types into
  // their banking app.
  return new Intl.NumberFormat("es-UY", {
    style: "currency",
    currency: currency || "UYU",
    minimumFractionDigits: 2,
  }).format(major);
}

/**
 * One detail row with an icon copy button.
 *
 * An icon rather than a "Copiar" text link: every comparable flow surveyed
 * (OKX, Wise, Revolut, Gemini, Airwallex) uses an icon here, and five text
 * links stacked down the right edge read as navigation — the one thing these
 * emphatically are not.
 */
function DetailRow({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    // Clipboard can reject (permissions, insecure context). The value is on
    // screen either way, so a failure must not look like an error.
    void navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => undefined);
  }, [value]);

  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-3 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            "font-mono tabular-nums text-foreground",
            emphasis ? "text-base font-semibold" : "text-sm font-medium",
          )}
        >
          {value}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={copy}
          aria-label={`${t("requests.user.bankTransfer.copy")}: ${label}`}
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-600" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
        </Button>
      </div>
    </div>
  );
}

/** A numbered step heading, so the two things the consumer must do are countable. */
function StepHeading({ n, children }: { n: number; children: ReactNode }) {
  return (
    <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
        {n}
      </span>
      {children}
    </h2>
  );
}

export function BankTransferPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const checkoutId = params.get("checkout_id") ?? "";

  const [phase, setPhase] = useState<Phase>("instructions");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [account, setAccount] = useState<BankTransferAccountResponse | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [amount, setAmount] = useState<{ amountMinor: number; currency: string } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  /**
   * Create the request from the stored receipt + the IndexedDB blob.
   *
   * The runner does NOT throw when creation fails — it toasts and returns,
   * because on the dLocal flow the consumer is sitting on a URL whose refresh
   * re-runs it. We are not on that URL, so success has to be detected rather
   * than assumed: without this the page sat on "Creando tu solicitud…" forever
   * while the real error (an active request already exists) flashed past in a
   * toast.
   */
  /**
   * Set before any create is attempted. A ref rather than state because
   * runCreate is a dependency of the effect that fills it — reading it through
   * state would re-create the callback and re-run the effect.
   */
  const quoteRequestIdRef = useRef<string | null>(null);
  /** The charge itemised, for the brand panel. Server-built, like the card page's. */
  const [items, setItems] = useState<Array<{ title: string; amountMinor: number }>>([]);
  /**
   * Whether a comprobante is already stored server-side. NOT derivable from
   * `busy`: when the page loads with transferSubmittedAt set and the create
   * that follows fails, phase leaves the busy states and a receipt still
   * exists — which is exactly when offering a card again is wrong.
   */
  const [receiptSubmitted, setReceiptSubmitted] = useState(false);
  /** Mirrors the card page's own guard: a second click during the round-trip
   *  would POST again and navigate twice. */
  const [switchingToCard, setSwitchingToCard] = useState(false);
  /** Which provider this deployment takes cards with, from the checkout row. */
  const [cardProvider, setCardProvider] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<{
    includeRut: boolean;
    rutDigits: string | null;
    legalName: string | null;
    address: string | null;
  } | null>(null);
  const [invoiceSuggestion, setInvoiceSuggestion] = useState<
    { rutDigits: string; legalName: string; address: string } | null
  >(null);

  const runCreate = useCallback(async () => {
    if (!checkoutId) return;
    /**
     * AN RFQ CHECKOUT HAS NOTHING TO CREATE HERE. This page was built for the
     * catalog wizard, where the request does not exist yet and the resume
     * runner replays the consumer's answers out of IndexedDB to build it. An
     * awarded quote already HAS its request: the legs are claimed, and the
     * child requests spawn server-side when the checkout becomes paid — which
     * for a transfer is when the receipt is approved, not when it is uploaded.
     *
     * Without this branch the runner would find no stored answers and show
     * "no pudimos crear tu solicitud" to someone who has just transferred real
     * money against a request that is sitting there, fine.
     */
    if (quoteRequestIdRef.current) {
      toast.success(t("requests.user.bankTransfer.quoteReceiptReceived"));
      navigate(`/requests/${quoteRequestIdRef.current}?source=quote`);
      return;
    }
    setPhase("creating");
    setCreateError(null);
    let created = false;
    try {
      await runCertificatePaymentResumeFlow({
        checkoutId,
        cancelled: () => false,
        navigate,
        onRequestCreated: () => {
          created = true;
        },
        onCreateFailed: (message) => setCreateError(message),
        queryClient,
        t,
      });
    } catch {
      created = false;
    }
    // On success the runner has already navigated to the request; leaving the
    // phase alone avoids a flash of this page's chrome on the way out.
    if (!created) setPhase("createFailed");
  }, [checkoutId, navigate, queryClient, t]);

  useEffect(() => {
    if (!isUuidParam(checkoutId)) {
      setLoadError(true);
      return;
    }
    let cancelled = false;
    void Promise.all([getCertificateCheckout(checkoutId), getBankTransferAccount()])
      .then(([row, acct]) => {
        if (cancelled) return;
        setAmount({ amountMinor: row.amountMinor, currency: row.currency });
        setReference(row.transferReference ?? null);
        quoteRequestIdRef.current =
          row.pricingSource === "quote" ? (row.quoteRequestId ?? null) : null;
        setItems(row.items ?? []);
        setReceiptSubmitted(row.transferSubmittedAt != null);
        setCardProvider(row.cardProvider ?? null);
        setInvoice(
          row.invoice ?? { includeRut: false, rutDigits: null, legalName: null, address: null },
        );
        setInvoiceSuggestion(row.invoiceSuggestion ?? null);
        setAccount(acct.configured ? acct : null);
        // The receipt is already stored (a refresh, or a return after request
        // creation failed). Do NOT redirect away and do NOT invite a second
        // upload — re-run the creation from the same IndexedDB blob. This IS
        // the recovery path: the resume runner deliberately leaves the blob in
        // place until a request exists, and bouncing to /requests here would
        // strand a consumer who has already transferred real money.
        if (row.transferSubmittedAt) {
          void runCreate();
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [checkoutId, navigate, runCreate]);

  const amountLabel = useMemo(
    () => (amount ? formatAmount(amount.amountMinor, amount.currency) : null),
    [amount],
  );

  const submit = useCallback(async () => {
    if (!receipt || !checkoutId) return;
    setPhase("uploading");
    try {
      await uploadTransferReceipt(checkoutId, receipt);
    } catch {
      setPhase("instructions");
      toast.error(t("requests.user.bankTransfer.uploadFailed"));
      return;
    }
    // The receipt is stored and the checkout is now request-eligible. Creation
    // reuses the same resume path as the card flow, so answers and documents
    // replay from IndexedDB exactly as after a dLocal round-trip. If this half
    // fails the receipt is NOT lost — it is already server-side, and this page
    // re-runs creation on reload.
    await runCreate();
  }, [receipt, checkoutId, t, runCreate]);

  const busy = phase === "uploading" || phase === "creating";

  /**
   * CAN THE CARD ACTUALLY BE PAID FROM SOMEWHERE? Switching back resolves the
   * provider from config, and dLocal has no page of its own — its payment is
   * created when Pagar is pressed and the consumer is redirected away.
   *
   * For an RFQ that is fine: Pagar lives on the request. For a CATALOG checkout
   * the request does not exist yet, so a dLocal deployment would leave a
   * pending card checkout with no entry point at all. Rather than offer a
   * button that strands them, it is offered only where a destination exists.
   */
  const canOfferCard = cardProvider === "mercadopago" || quoteRequestIdRef.current != null;

  return (
    /**
     * The same split as the card page, through the same component: these are
     * one payment seen from two sides, and the consumer who switches between
     * them should not feel they changed products.
     */
    <div className="lg:grid lg:min-h-svh lg:grid-cols-2">
      <CheckoutSummaryPanel
        amountMinor={amount?.amountMinor ?? null}
        currency={amount?.currency ?? "UYU"}
        items={items}
        /**
         * BACK TO WHERE THEY CAME FROM. This sent everyone to /requests, which
         * for an awarded RFQ is the wrong place twice over: the consumer loses
         * the request they were paying for, and the list does not explain why
         * they are suddenly looking at it.
         *
         * Still deliberately NOT browser history: a consumer who arrived from
         * the catalog wizard would land on a wizard whose checkout already
         * exists, and be invited to create a duplicate.
         */
        backHref={
          quoteRequestIdRef.current
            ? `/requests/${quoteRequestIdRef.current}?source=quote`
            : "/requests"
        }
        onBack={() =>
          navigate(
            quoteRequestIdRef.current
              ? `/requests/${quoteRequestIdRef.current}?source=quote`
              : "/requests",
          )
        }
        backDisabled={busy}
      />

      <main className="w-full px-4 py-8 md:px-10 md:py-14">
        <div className="w-full lg:max-w-lg">
        {loadError ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-foreground">{t("requests.user.bankTransfer.notFound")}</p>
              <Button type="button" className="mt-5" onClick={() => navigate("/requests")}>
                {t("requests.user.bankTransfer.backToRequests")}
              </Button>
            </CardContent>
          </Card>
        ) : phase === "createFailed" ? (
          /**
           * The consumer's money is CONFIRMED at this point — the checkout is
           * paid and the receipt is stored server-side. Leading with the
           * failure would read as "your payment did not go through", which is
           * both false and the most alarming possible reading. Lead with what
           * is true and reassuring, then the caveat, then the reason.
           *
           * "Our team can see this" is only written because it is now true:
           * a paid bank-transfer checkout with no request surfaces at the TOP
           * of the admin review queue (listForReview). Do not keep this line
           * if that ever stops being the case.
           */
          <Card>
            <CardContent className="py-8">
              <div className="flex flex-col items-center text-center">
                <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <Check className="h-5 w-5 text-green-600 dark:text-green-400" aria-hidden />
                </span>
                <h2 className="text-base font-semibold text-foreground">
                  {t("requests.user.bankTransfer.createFailedTitle")}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("requests.user.bankTransfer.createFailedBody")}
                </p>
              </div>

              <div className="mt-5 space-y-2.5 rounded-lg border border-border bg-background p-3.5">
                <p className="flex items-start gap-2.5 text-sm text-foreground">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden />
                  {t("requests.user.bankTransfer.createFailedReassure")}
                </p>
                <p className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  {t("requests.user.bankTransfer.createFailedNotified")}
                </p>
              </div>

              {createError ? (
                <div className="mt-3 rounded-lg bg-muted px-3.5 py-2.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t("requests.user.bankTransfer.createFailedReasonLabel")}
                  </p>
                  <p className="mt-1 text-sm text-foreground">{createError}</p>
                </div>
              ) : null}

              <div className="mt-6 flex flex-col gap-2">
                <Button type="button" size="lg" onClick={() => void runCreate()}>
                  {t("requests.user.bankTransfer.retry")}
                </Button>
                <Button type="button" variant="ghost" onClick={() => navigate("/requests")}>
                  {t("requests.user.bankTransfer.backToRequests")}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : busy ? (
          <div className="py-16 text-center">
            <LoadingLogo />
            <p className="mt-4 text-muted-foreground">
              {phase === "uploading"
                ? t("requests.user.bankTransfer.uploading")
                : t("requests.user.bankTransfer.creating")}
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-foreground md:text-2xl">
              {t("requests.user.bankTransfer.title")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("requests.user.bankTransfer.intro")}
            </p>

            <section className="mt-8">
              <StepHeading n={1}>{t("requests.user.bankTransfer.step1")}</StepHeading>
              <Card>
                <CardContent className="px-4 py-1">
                  {amountLabel ? (
                    <DetailRow
                      label={t("requests.user.bankTransfer.amount")}
                      value={amountLabel}
                      emphasis
                    />
                  ) : null}
                  {account?.configured ? (
                    <>
                      <DetailRow
                        label={t("requests.user.bankTransfer.bank")}
                        value={account.bankShortName ?? ""}
                      />
                      <DetailRow
                        label={t("requests.user.bankTransfer.holder")}
                        value={account.accountHolder ?? ""}
                      />
                      <DetailRow
                        label={t("requests.user.bankTransfer.account")}
                        value={account.accountNumber ?? ""}
                      />
                      {reference ? (
                        <DetailRow
                          label={t("requests.user.bankTransfer.reference")}
                          value={reference}
                        />
                      ) : null}
                    </>
                  ) : (
                    <p className="py-4 text-sm text-muted-foreground">
                      {t("requests.user.bankTransfer.instructionsUnavailable")}
                    </p>
                  )}
                </CardContent>
              </Card>

              {reference ? (
                <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-border bg-background p-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <p className="text-xs text-muted-foreground">
                    {t("requests.user.bankTransfer.referenceHint")}
                  </p>
                </div>
              ) : null}
            </section>

            <section className="mt-8">
              <StepHeading n={2}>{t("requests.user.bankTransfer.step2")}</StepHeading>
              <StyledFileInput
                id="transfer-receipt"
                accept={ACCEPTED}
                value={receipt}
                onChange={setReceipt}
                hint={t("requests.user.bankTransfer.receiptHint")}
              />
            </section>

            {/* Asked here too, and against the same checkout: a consumer who
                pays by transfer wants the same factura a card payer does, and
                the question belongs to the payment rather than to the method. */}
            {invoice && checkoutId ? (
              <div className="mt-6">
                <CheckoutInvoiceFields
                  checkoutId={checkoutId}
                  initial={invoice}
                  suggestion={invoiceSuggestion}
                  disabled={busy || receiptSubmitted}
                />
              </div>
            ) : null}

            <Button
              type="button"
              disabled={!receipt}
              onClick={() => void submit()}
              className="mt-6 w-full"
              size="lg"
            >
              {t("requests.user.bankTransfer.submit")}
            </Button>

            {/**
             * THE CARD, AS A REAL CHOICE. Transfer leads because it costs the
             * platform less, so the alternative has to be visible enough that
             * nobody feels funnelled — a text link under the fold read as fine
             * print, which is the wrong way to be cheaper.
             *
             * Hidden once a receipt exists (receiptSubmitted, not busy — the
             * two part company when a create fails after an upload). By then
             * the transfer is a claim on real money awaiting a human, and
             * offering a card beside it is how one job gets paid twice. The
             * server refuses it too.
             *
             * And only where a card can actually be paid: switching back
             * resolves the provider from config, and dLocal has no page of its
             * own, so a catalog checkout on a dLocal deployment would have
             * nowhere to go.
             */}
            {!busy && !receiptSubmitted && canOfferCard ? (
              <>
                <div className="my-5 flex items-center gap-3">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">
                    {t("requests.user.bankTransfer.orPayAnotherWay")}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={switchingToCard}
                  onClick={() => {
                    if (!checkoutId) return;
                    setSwitchingToCard(true);
                    void switchCheckoutToCard(checkoutId)
                      .then((res) => {
                        /**
                         * ROUTED BY WHAT THE SERVER ACTUALLY CHOSE. It resolves
                         * the provider from config, which is dLocal on most
                         * deployments — and the Mercado Pago page refuses a
                         * checkout that is not its own and bounces to
                         * /requests, which would strand the consumer with a
                         * card checkout and no way to pay it.
                         *
                         * dLocal has no page of its own: its payment is created
                         * when Pagar is pressed and the consumer is redirected
                         * away, so the request is where they need to be.
                         */
                        if (res.paymentMethod === "mercadopago") {
                          window.location.assign(
                            `/pay/mercadopago?checkout_id=${encodeURIComponent(checkoutId)}`,
                          );
                          return;
                        }
                        navigate(
                          quoteRequestIdRef.current
                            ? `/requests/${quoteRequestIdRef.current}?source=quote`
                            : "/requests",
                        );
                      })
                      .catch((err: unknown) => {
                        setSwitchingToCard(false);
                        // The server's own sentence when it has one: "ya
                        // subiste un comprobante" is the actionable half.
                        toast.error(
                          serverMessage(err) ??
                            t("requests.user.bankTransfer.switchToCardFailed"),
                        );
                      });
                  }}
                  className="w-full gap-2"
                >
                  <CreditCard className="h-4 w-4" aria-hidden />
                  {t("requests.user.bankTransfer.payWithCardInstead")}
                </Button>
              </>
            ) : null}

            <p className="mt-3 text-center text-xs text-muted-foreground">
              {t("requests.user.bankTransfer.afterSubmit")}
            </p>
          </>
        )}
        </div>
      </main>
    </div>
  );
}
